/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ASCENDING_KEY, DATE_SORT_KEYS, DESCENDING_KEY } from "@eisinc/XDL/constants";
import { InvoicePaymentModel, SortInvoicePaymentByField } from "@eisinc/XDL/interfaces";
import { SortOrderOptions } from "@insite/mobius/DataTable";

export const handleSortInvoicePayments = (
    invoicePayments: InvoicePaymentModel[] | undefined,
    selectedSortKey: keyof InvoicePaymentModel,
    sortOrder: SortInvoicePaymentByField,
) => {
    return invoicePayments?.sort((a, b) => {
        const aKeyValue = a[selectedSortKey] as any;
        const bKeyValue = b[selectedSortKey] as any;

        if (DATE_SORT_KEYS.includes(selectedSortKey as any)) {
            const dateA = new Date(aKeyValue).getTime();
            const dateB = new Date(bKeyValue).getTime();

            if (dateA < dateB) {
                return sortOrder[selectedSortKey] === ASCENDING_KEY ? -1 : 1;
            }
            if (dateA > dateB) {
                return sortOrder[selectedSortKey] === ASCENDING_KEY ? 1 : -1;
            }
        } else {
            if (aKeyValue < bKeyValue) {
                return sortOrder[selectedSortKey] === ASCENDING_KEY ? -1 : 1;
            }
            if (aKeyValue > bKeyValue) {
                return sortOrder[selectedSortKey] === ASCENDING_KEY ? 1 : -1;
            }
        }
        return 0;
    });
};

const getSortingFieldsArrow = (sortField: string, sortOrder: SortInvoicePaymentByField) => {
    let arrowDirection = "";
    if (sortOrder[sortField] === DESCENDING_KEY || !sortOrder[sortField]) {
        arrowDirection = ASCENDING_KEY;
    } else if (sortOrder[sortField] === ASCENDING_KEY) {
        arrowDirection = DESCENDING_KEY;
    }

    return arrowDirection as SortOrderOptions;
};

export const handleSwitchSortArrow = (sortField: keyof InvoicePaymentModel, sortOrder: SortInvoicePaymentByField) => {
    const updateSortOrder = { ...sortOrder };
    Object.keys(sortOrder).forEach(key => {
        if (key === sortField) {
            updateSortOrder[key] = getSortingFieldsArrow(sortField, sortOrder);
            return;
        }
        updateSortOrder[key] = null;
    });
    return updateSortOrder;
};

export const getPaginatedInvoicePayments = (
    page: number,
    pageSize: number,
    invoicePayments: InvoicePaymentModel[] | undefined,
) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    const paginatedInvoicePayments = invoicePayments?.slice(startIndex, endIndex);

    return paginatedInvoicePayments;
};

export const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

export const calcSelectedInvoicesTotal = (invoices: InvoicePaymentModel[]) => {
    const totalAmount = invoices.reduce(
        (acc, item) => acc + ((item.amountToPay && parseFloat(item.amountToPay as any)) || item.invoiceAmount || 0),
        0,
    );
    return totalAmount;
};
